import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import type { ContractShareLink } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Link2, Copy, Check, Trash2 } from 'lucide-react';

const MODE_LABELS: Record<string, string> = {
    add: 'Add Only',
    remove: 'Remove Only',
    both: 'Add & Remove',
};

export function ShareLinks({ contractId, isDom }: { contractId: number; isDom: boolean }) {
    const queryClient = useQueryClient();
    const [showCreate, setShowCreate] = useState(false);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        label: '',
        mode: 'add' as string,
        min_amount: '',
        max_amount: '',
        require_login: false,
    });

    const { data } = useQuery({
        queryKey: ['share-links', contractId],
        queryFn: () => api.get<{ data: ContractShareLink[] }>(`/contracts/${contractId}/share-links`),
    });

    const createMutation = useMutation({
        mutationFn: (data: unknown) => api.post<{ data: ContractShareLink }>(`/contracts/${contractId}/share-links`, data),
        onSuccess: () => {
            trackEvent('share_link_created', { contract_id: contractId });
            setShowCreate(false);
            setForm({ label: '', mode: 'add', min_amount: '', max_amount: '', require_login: false });
            setError('');
            queryClient.invalidateQueries({ queryKey: ['share-links', contractId] });
        },
        onError: (err: Error) => setError(err instanceof ApiError ? err.message : 'Failed to create link'),
    });

    const deleteMutation = useMutation({
        mutationFn: (linkId: number) => api.delete(`/contracts/${contractId}/share-links/${linkId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['share-links', contractId] }),
    });

    const toggleMutation = useMutation({
        mutationFn: ({ linkId, active }: { linkId: number; active: boolean }) =>
            api.put(`/contracts/${contractId}/share-links/${linkId}`, { is_active: active }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['share-links', contractId] }),
    });

    const links = data?.data ?? [];

    const copyLink = (link: ContractShareLink) => {
        const url = `${window.location.origin}/share/${link.token}`;
        navigator.clipboard?.writeText(url).then(() => {
            setCopiedId(link.id);
            setTimeout(() => setCopiedId(null), 2000);
        });
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                    <Link2 className="h-4 w-4" />
                    Share Links
                </CardTitle>
                {isDom && (
                    <Button size="sm" variant="outline" onClick={() => setShowCreate(!showCreate)}>
                        {showCreate ? 'Cancel' : '+ New Link'}
                    </Button>
                )}
            </CardHeader>
            <CardContent className="space-y-4">
                {showCreate && (
                    <div className="space-y-3 rounded border border-slate-700 p-3">
                        <Input
                            placeholder="Label (e.g. Tip Jar, Bailout)"
                            value={form.label}
                            onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                        />
                        <div>
                            <label className="text-xs text-slate-400">Mode</label>
                            <select
                                className="w-full rounded border border-slate-700 bg-slate-900 px-2 py-2 text-sm text-slate-200"
                                value={form.mode}
                                onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
                            >
                                <option value="add">Add to Balance</option>
                                <option value="remove">Remove from Balance</option>
                                <option value="both">Both Add & Remove</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="text-xs text-slate-400">Min Amount ($)</label>
                                <Input type="number" min="0.01" step="0.01" value={form.min_amount} onChange={e => setForm(f => ({ ...f, min_amount: e.target.value }))} placeholder="Optional" />
                            </div>
                            <div>
                                <label className="text-xs text-slate-400">Max Amount ($)</label>
                                <Input type="number" min="0.01" step="0.01" value={form.max_amount} onChange={e => setForm(f => ({ ...f, max_amount: e.target.value }))} placeholder="Optional" />
                            </div>
                        </div>
                        <label className="flex items-center gap-2 text-xs text-slate-400">
                            <input
                                type="checkbox"
                                checked={form.require_login}
                                onChange={e => setForm(f => ({ ...f, require_login: e.target.checked }))}
                                className="rounded border-slate-600"
                            />
                            Require login to use
                        </label>
                        {error && <p className="text-xs text-red-400">{error}</p>}
                        <Button
                            size="sm"
                            disabled={createMutation.isPending}
                            onClick={() => createMutation.mutate({
                                label: form.label || undefined,
                                mode: form.mode,
                                require_login: form.require_login,
                                ...(form.min_amount ? { min_amount: parseFloat(form.min_amount) } : {}),
                                ...(form.max_amount ? { max_amount: parseFloat(form.max_amount) } : {}),
                            })}
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Share Link'}
                        </Button>
                    </div>
                )}

                {links.length === 0 && !showCreate && (
                    <p className="text-sm text-slate-500 italic">No share links yet.</p>
                )}

                {links.map(link => (
                    <div key={link.id} className="rounded border border-slate-700 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm">{link.label || 'Untitled Link'}</span>
                                <Badge variant={link.is_active ? 'default' : 'secondary'}>
                                    {link.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                                <Badge variant="outline" className="text-[10px]">
                                    {MODE_LABELS[link.mode]}
                                </Badge>
                            </div>
                            {isDom && (
                                <div className="flex items-center gap-1">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-xs"
                                        onClick={() => toggleMutation.mutate({ linkId: link.id, active: !link.is_active })}
                                    >
                                        {link.is_active ? 'Disable' : 'Enable'}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        className="text-red-400 text-xs"
                                        onClick={() => deleteMutation.mutate(link.id)}
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div className="text-xs text-slate-400 space-y-0.5">
                            {link.min_amount && <span>Min: ${parseFloat(link.min_amount).toFixed(2)} </span>}
                            {link.max_amount && <span>Max: ${parseFloat(link.max_amount).toFixed(2)} </span>}
                            {link.require_login && <span>Login required </span>}
                            {link.uses_count !== undefined && <span>· {link.uses_count} uses</span>}
                        </div>
                        <Button
                            size="sm"
                            variant="outline"
                            className="text-xs"
                            onClick={() => copyLink(link)}
                        >
                            {copiedId === link.id ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                            {copiedId === link.id ? 'Copied!' : 'Copy Link'}
                        </Button>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}
